import React from "react";
import Barchart from "./Barchart";
import { Helmet } from "react-helmet-async";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import moment from "moment";
import secureLocalStorage from "react-secure-storage";
import { colors } from "../brand";
import { getOutstandingSummary } from "../Api/index";

const money = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function StatTile({ label, count, amount, accent, note }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "14px",
        border: `1px solid ${colors.border}`,
        borderTop: `3px solid ${accent}`,
        background: colors.surface,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: colors.textSecondary,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: colors.textPrimary, mt: 0.5 }}>
        {count.toLocaleString()}
      </Typography>
      <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
        {amount !== undefined ? `GHS ${money.format(amount)}` : note}
      </Typography>
    </Paper>
  );
}

export default function Insight() {
  const navigate = useNavigate();
  const user = JSON.parse(secureLocalStorage.getItem("profile"));

  // Totals come worked out from the server, not every item
  const [summary, setSummary] = React.useState(null);
  React.useEffect(() => {
    let mounted = true;
    getOutstandingSummary()
      .then(({ data }) => {
        if (mounted) setSummary(data.results);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const isValidDate = (date) => Boolean(date) && !isNaN(new Date(date));
  const maxDate = summary?.latestDate;
  const debit = summary?.debit || { count: 0, amount: 0 };
  const credit = summary?.credit || { count: 0, amount: 0 };

  const results = user?.results || {};

  return (
    <Box>
      <Helmet title="Dashboard | Reconciliation" />

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: colors.textPrimary }}>
            Welcome back, {results.firstName}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: colors.textSecondary }}>
            {results.reconciliation ? `${results.reconciliation} · ` : ""}
            {isValidDate(maxDate)
              ? `Latest reconciliation date ${moment(maxDate).format(
                  "D MMM YYYY",
                )} (${moment(maxDate).fromNow()})`
              : "No reconciliation has been prepared yet"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            disableElevation
            startIcon={<CompareArrowsIcon />}
            onClick={() => navigate("/reconciliation/new-reconciliation")}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              backgroundColor: colors.primary,
              "&:hover": { backgroundColor: colors.primaryDark },
            }}
          >
            New reconciliation
          </Button>
          <Button
            variant="outlined"
            startIcon={<ReceiptLongOutlinedIcon />}
            onClick={() => navigate("/reconciliation/single-reconciliation")}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              color: colors.primary,
              borderColor: colors.border,
            }}
          >
            Single statement
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 2.5,
        }}
      >
        <StatTile
          label="Open outstanding items"
          count={summary?.count || 0}
          amount={debit.amount + credit.amount}
          accent={colors.primary}
        />
        <StatTile
          label="Outstanding debits"
          count={debit.count}
          amount={debit.amount}
          accent={colors.danger}
        />
        <StatTile
          label="Outstanding credits"
          count={credit.count}
          amount={credit.amount}
          accent={colors.secondary}
        />
        <StatTile
          label="Older than 30 days"
          count={summary?.aged || 0}
          accent="#f59e0b"
          note="Age at the last reconciliation"
        />
      </Box>

      <Barchart branches={summary?.branches || []} />
    </Box>
  );
}
